import { useFormik } from 'formik';
// mantine
import { Button, Select, TextInput, UnstyledButton } from '@mantine/core';
// utils
import { processFormData } from '@newo/toolkit/bff';
import { TProject } from '@newo/api/gateway';
import { useEffect } from 'react';
import { tzDatetime } from '@/modules/DateTime/utils';
import { useProjectImageStoragesQuery } from '@/services/designer/project_image_storages/project_image_storages.queries';
import validationSchema from '@/pages/Projects/components/ProjectImageForm/ProjectImageForm.schema';

type ProjectImageFormProps = {
    project?: TProject;
    onCancel?: () => void;
    onSubmit?: (data: { idn: string; storage_id: string }) => Promise<void>;
};

const ProjectImageForm = ({
    project,
    onCancel,
    onSubmit
}: ProjectImageFormProps) => {
    const { data: storages = [] } = useProjectImageStoragesQuery();

    const formik = useFormik({
        initialValues: {
            idn: `${project?.idn}_${tzDatetime().toFormat('yyyy_LL_dd')}`,
            storage_id: ''
        },
        validationSchema,
        async onSubmit(formData) {
            const data = processFormData(formData, false);
            await onSubmit?.(data);
            formik.setSubmitting(false);
        }
    });

    useEffect(() => {
        if (storages.at(0)) {
            formik.setFieldValue('storage_id', storages.at(0)?.id);
        }
    }, [storages]);

    const handleCancel = () => {
        onCancel?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Idn"
                name="idn"
                placeholder="Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Select
                withAsterisk
                data={storages.map((storage) => ({
                    value: storage.id,
                    label: storage.title
                }))}
                error={
                    formik.touched.storage_id && formik.values.storage_id
                        ? formik.errors.storage_id
                        : undefined
                }
                label="Storage"
                name="storage_id"
                placeholder="Select storage"
                value={formik.values.storage_id}
                onChange={async (value) =>
                    formik.setFieldValue('storage_id', value)
                }
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Create
                </Button>
            </div>
        </form>
    );
};

export default ProjectImageForm;
